import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  ButtonDS,
  DownloadOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  TextCapitalized,
  sortByDateTime,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { selectCompany } from "../homeSlice";
import { PublicAsfInvoices, PublicAsfInvoicesDisplayed } from "./billingsAPI";
import {
  asfInvoicesExportAsync,
  asfInvoicesListAsync,
  documentDownloadAsync,
  selecAsfInvoicesExportLink,
  selectAsfInvoicesExportStatus,
  selectAsfInvoicesList,
  selectAsfInvoicesListStatus,
} from "./billingsSlice";
import "../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";

export const AsfInvoicesTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);
  const navigate = useNavigate();

  const [asfInvoicesListSorted, setAsfInvoicesListSorted] = useState<
    PublicAsfInvoicesDisplayed[] | null
  >([]);
  const asfInvoicesListStatus = useAppSelector(selectAsfInvoicesListStatus);
  const asfInvoicesListInit: PublicAsfInvoices[] = useAppSelector(
    selectAsfInvoicesList,
  );
  const asfInvoicesExportLink = useAppSelector(selecAsfInvoicesExportLink);
  const asfInvoicesExportStatus = useAppSelector(selectAsfInvoicesExportStatus);

  const pageName = "asfInvoices";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      beginningDate: "size",
      endDate: "size",
      transactionsAmountTtc: "sum",
      transactionsNumber: "sum",
      fileName: "size",
      createdAt: "size",
      bucketFileNameDownload: "size",
      bucketFileNameDownloadXlsx: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const asfInvoicesList: PublicAsfInvoices[] = useMemo(() => {
    const rowsWithId = asfInvoicesListInit.map((asfInvoice) => {
      const newDict: PublicAsfInvoices & { id: string } = {
        ...asfInvoice,
        id: asfInvoice.uuid,
      };
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [asfInvoicesListInit]);

  useEffect(() => {
    if (company && !company.canOrderBadges && params.tab === "asfInvoices") {
      navigate(`/home`);
    }
  }, [company, navigate, params.tab]);

  useEffect(() => {
    if (company && asfInvoicesListStatus === "idle") {
      void dispatch(asfInvoicesListAsync(company.uuid));
    }
  }, [dispatch, company, asfInvoicesListStatus]);

  useEffect(() => {
    if (asfInvoicesExportStatus === "success") {
      triggerToast(t("billings.asfInvoices.upload.success") || "", "valid");
    } else if (asfInvoicesExportStatus === "failed") {
      triggerToast(t("billings.asfInvoices.upload.failure") || "", "error");
    }
  }, [asfInvoicesExportStatus, t]);

  useEffect(() => {
    if (asfInvoicesList && asfInvoicesListStatus === "success") {
      const asfInvoicesListSorted = sortByDateTime(asfInvoicesList, "desc");
      setAsfInvoicesListSorted(asfInvoicesListSorted);
    }
  }, [asfInvoicesListStatus, asfInvoicesList]);

  useEffect(() => {
    if (asfInvoicesExportLink.presignedUrl && asfInvoicesExportLink.fileName) {
      void dispatch(documentDownloadAsync(asfInvoicesExportLink));
    }
  }, [asfInvoicesExportLink, dispatch]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("asfInvoices.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof PublicAsfInvoicesDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      beginningDate: {
        type: "date",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      endDate: {
        type: "date",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      transactionsAmountTtc: {
        type: "number",
        width: 100,
        valueFormatter: (value: number | null) => {
          return value ? `${value.toFixed(2)} € TTC` : "0 € TTC";
        },
      },
      transactionsNumber: {
        type: "number",
        width: 100,
        valueFormatter: (value: number | null) => {
          return value ? `${value} transactions` : "0 transactions";
        },
      },
      fileName: {
        type: "string",
        width: 200,
      },
      createdAt: {
        type: "date",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      bucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      asfInvoicesExportAsync({
                        bucketFileName: value ?? "",
                        fileName:
                          "fileName" in row ? (row["fileName"] as string) : "",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
      bucketFileNameDownloadXlsx: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      asfInvoicesExportAsync({
                        bucketFileName: value ?? "",
                        fileName:
                          "fileName" in row
                            ? (row["fileName"] as string).split(".pdf")[0] +
                              ".xlsx"
                            : "",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
    };
  }, [dispatch]);

  if (params.tab !== "asfInvoices") {
    return;
  }

  const searchBarProps = {
    values: asfInvoicesList,
    setFilterValues: setAsfInvoicesListSorted,
    keysToIgnore: [
      "bucketFileNameDownload" as const,
      "bucketFileNameDownloadXlsx" as const,
      "uuid" as const,
    ],
  };

  if (!asfInvoicesListSorted) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<
      keyof PublicAsfInvoicesDisplayed,
      PublicAsfInvoicesDisplayed
    >
      rows={asfInvoicesListSorted}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
